/**
 * Unsaved editor buffers, and the mapping from those buffers to the atomic
 * change list the backend expects.
 *
 * The backend commits a whole `changes` array as ONE revision, so every dirty
 * buffer must go out in a single save — one revision per user intent, not one
 * per file. Pure and React-free so the mapping is unit-testable.
 */
import type { CanvasFile } from "../types";

export interface Draft {
  /** Editor buffer, keyed by path. Only dirty paths appear. */
  content: string;
  /** The sha256 this buffer was opened against; null for a new file. */
  baseSha: string | null;
}

export type Drafts = Record<string, Draft>;

export interface CanvasChange {
  op: "write" | "delete";
  path: string;
  content?: string;
  expected_sha256: string | null;
}

export function shaFor(files: CanvasFile[] | undefined, path: string): string | null {
  return files?.find(f => f.path === path)?.sha256 ?? null;
}

/**
 * The change list for every dirty buffer.
 *
 * `expected_sha256` is the optimistic-concurrency guard: it must be the hash
 * the buffer was opened against, so a revision written by the assistant while
 * the user was typing is rejected with a 409 rather than silently clobbered.
 */
export function draftChanges(drafts: Drafts): CanvasChange[] {
  return Object.entries(drafts).map(([path, draft]) => ({
    op: "write",
    path,
    content: draft.content,
    expected_sha256: draft.baseSha,
  }));
}

export function writeChange(path: string, content: string, expected: string | null): CanvasChange {
  return { op: "write", path, content, expected_sha256: expected };
}

export function deleteChange(path: string, expected: string | null): CanvasChange {
  return { op: "delete", path, expected_sha256: expected };
}

/** A rename is a write of the new path plus a delete of the old, in one revision. */
export function renameChanges(from: string, to: string, content: string, expected: string | null): CanvasChange[] {
  return [writeChange(to, content, null), deleteChange(from, expected)];
}

export function summarize(changes: CanvasChange[]): string {
  if (changes.length === 1) {
    const only = changes[0];
    return `${only.op === "delete" ? "Delete" : "Edit"} ${only.path}`;
  }
  return `Edit ${changes.length} files`;
}
